import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Box, TextField, InputAdornment, CircularProgress, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Typography, Chip, Button, Pagination, TableSortLabel, Tooltip,
} from '@mui/material';
import { Search as SearchIcon, LinkOff as MatchIcon } from '@mui/icons-material';
import fundedAPI from '../../api/funded';
import FundedNav from './FundedNav';
import MatchDepositModal from '../../components/funded/MatchDepositModal';
import { currency, fmtDate } from '../../components/funded/format';

export default function UnmatchedView() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, pages: 1, sumAmount: 0 });
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [matchTarget, setMatchTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fundedAPI.getUnmatched({ search, dateFrom, dateTo, sortBy, order, page, limit: 50 });
      setRows(res.data.data || []);
      setMeta({ total: res.data.total, page: res.data.page, pages: res.data.pages, sumAmount: res.data.sumAmount });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [search, dateFrom, dateTo, sortBy, order, page]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const handleSort = (col) => {
    if (sortBy === col) setOrder(order === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setOrder('desc'); }
    setPage(1);
  };

  const cols = [
    { id: 'date', label: 'Date', sortable: true },
    { id: 'amount', label: 'Amount', sortable: true, align: 'right' },
    { id: 'lender', label: 'Lender', sortable: true },
    { id: 'sender', label: 'Sender', sortable: false },
    { id: 'notes', label: 'Notes', sortable: false },
    { id: 'actions', label: '', sortable: false },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <FundedNav
        title="Unmatched Deposits"
        subtitle="ACHs with no business_name — match to a client to move into fundings & commissions"
      />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <TextField
          size="small" placeholder="Search sender, lender, notes…"
          value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          sx={{ width: { xs: '100%', sm: 300 } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <TextField size="small" label="From" type="date" InputLabelProps={{ shrink: true }} value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
        <TextField size="small" label="To" type="date" InputLabelProps={{ shrink: true }} value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : (
        <>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {cols.map((c) => (
                    <TableCell key={c.id} align={c.align || 'left'} sx={{ fontWeight: 700, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                      {c.sortable ? (
                        <TableSortLabel active={sortBy === c.id} direction={sortBy === c.id ? order : 'asc'} onClick={() => handleSort(c.id)}>
                          {c.label}
                        </TableSortLabel>
                      ) : c.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.deposit_id} hover>
                    <TableCell>{fmtDate(r.date)}</TableCell>
                    <TableCell align="right"><Typography variant="body2" fontWeight={700}>{currency(r.amount)}</Typography></TableCell>
                    <TableCell><Chip label={r.lender || 'Unknown'} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} /></TableCell>
                    <TableCell>
                      <Tooltip title={r.sender || ''}>
                        <Typography variant="caption" noWrap sx={{ maxWidth: 260, display: 'block' }}>{r.sender || '—'}</Typography>
                      </Tooltip>
                      {r.basis && <Chip label={r.basis} size="small" sx={{ height: 16, fontSize: '0.6rem', mt: 0.3 }} />}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={r.notes || ''}>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 220, display: 'block' }}>{r.notes || '—'}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="contained" startIcon={<MatchIcon />} onClick={() => setMatchTarget(r)}>Match</Button>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={cols.length} align="center" sx={{ py: 4, color: 'text.secondary' }}>No unmatched deposits 🎉</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              {meta.total} deposit{meta.total === 1 ? '' : 's'} · total {currency(meta.sumAmount)}
            </Typography>
            {meta.pages > 1 && <Pagination count={meta.pages} page={meta.page} onChange={(_, p) => setPage(p)} size="small" />}
          </Stack>
        </>
      )}

      <MatchDepositModal
        open={!!matchTarget}
        deposit={matchTarget}
        onClose={() => setMatchTarget(null)}
        onMatched={load}
      />
    </Container>
  );
}
